import { GroupKey } from '@store/billingQueue';

export interface BillingQueueUploadItem {
  orderId: number;
  agent: string;
  officeName?: string | null;
  uploaded: string;
  flagId: number;
  uploadedBy: string;
  accountingGroup: number;
  needsPaperwork: number;
  lTL: boolean;
  quickpay: boolean;
  thirdParty: boolean;
  customerName: string;
  loadId: number;
  canadian: boolean;
  transfloStatus: string;
  autoProcessingExclusionReason: string | null;
  emptyRequirementMet: boolean;
  carrier: string;
  tmsPortalName?: string;
}
export interface DocumentRecord {
  id: number;
  batchId: string;
  correlationId: string;
  dateReceived: string;
  loadNumber: string;
  loadId: number | null;
  url: string;
  documents: string;
  problem: string;
  quickPay: boolean;
  readyToInvoice: boolean;
  accountingGroup: string | null;
}

export type LoadDocument = {
  loading: boolean;
  data: DocumentRecord[];
  filteredDataCount: number;
  reImportLoading: boolean;
  downloadLoading: boolean;
  groupType?: GroupKey;
};

export interface BillingQueueApprovedItem {
  orderId: number;
  flaggedBy: string;
  agent: string;
  officeName?: string | null;
  approvedDate: string;
  accountingGroup: number;
  approvedBy: string;
  lastCommentBy: string;
  lastComment: string;
  lastCommentDate: string;
  lastCommenterId: number;
  lTL: boolean;
  flagId: number;
  quickpay: boolean;
  thirdParty: boolean;
  customerName: string;
  canadian: boolean;
  transfloStatus: string;
  carrier: string;
  tmsPortalName?: string;
}

export interface BillingQueuePendingApprovalItem {
  id: number;
  lTL: boolean;
  description: string;
  setDate: string;
  setBy: string;
  order: number;
  agent: string;
  agentId: number;
  office: string;
  accountingGroup: number;
  lastCommentDate: string | null;
  lastComment: string | null;
  lastCommentBy: string | null;
  needsAttention: boolean;
  overdue: boolean;
  pastDue: boolean;
  thirdParty: boolean;
  quickpay: boolean;
  customerName: string;
  canadian: boolean;
  isDisputed: boolean;
  carrierName: string;
  currentState: string | null;
  stateChangedAt: string | null;
}
export interface BillingQueueRespondedItem {
  orderId: number;
  flaggedBy: string;
  agent: string;
  officeName?: string | null;
  approvedDate: string;
  accountingGroup: number;
  approvedBy: string;
  lastCommentBy: string;
  lastComment: string;
  lastCommentDate: string;
  lastCommenterId: number;
  lTL: boolean;
  flagId: number;
  quickpay: boolean;
  customerName: string;
  thirdParty: boolean;
  canadian: boolean;
  carrier: string;
}

export interface BillingQueueDisputedItem {
  id: number;
  lTL: boolean;
  description: string;
  setDate: string;
  setBy: string;
  order: number;
  agent: string;
  agentId: number;
  office: string;
  accountingGroup: number;
  lastCommentDate: string | null;
  lastComment: string | null;
  lastCommentBy: string | null;
  needsAttention: boolean;
  overdue: boolean;
  pastDue: boolean;
  thirdParty: boolean;
  quickpay: boolean;
  customerName: string;
  canadian: boolean;
  isDisputed: boolean;
  carrierName: string;
}

export interface DocumentHistoryItem {
  id: number;
  confirmationNumber: string;
  dateReceived: string;
  dateProcessed: string;
  s3Location: string;
  loadNumber: string;
  documents: string;
  loadId: number;
  exceptionReason: string;
  batchId: string;
}

export type UploadedDocuments = {
  'rate Confirmation'?: string[];
  'carrier Invoice'?: string[];
  'bill of Lading'?: string[];
  'supporting BOL'?: string[];
  email?: string[];
  'proof Of Delivery'?: string[];
  'lumper Receipt'?: string[];
  mISC?: string[];
  'notice of Assignment'?: string[];
  iNSP?: string[];
  dETN?: string[];
};

export type BillingQueueStoreType = {
  uploadQueue: {
    loading: boolean;
    data: BillingQueueUploadItem[];
    filteredDataCount: number;
  };
  approvedQueue: {
    loading: boolean;
    data: BillingQueueApprovedItem[];
    filteredDataCount: number;
  };
  pendingApprovalQueue: {
    loading: boolean;
    data: BillingQueuePendingApprovalItem[];
    filteredDataCount: number;
  };
  loadDocument: LoadDocument;
  respondedQueue: {
    loading: boolean;
    data: BillingQueueRespondedItem[];
    filteredDataCount: number;
  };
  disputedQueue: {
    loading: boolean;
    data: BillingQueueDisputedItem[];
    filteredDataCount: number;
  };
  loadDocumentMetaData: DocumentMetaDataRecord;
  noaDocument: {
    data: DocumentRecord[];
  };
  reviewDocument: {
    data: DocumentRecord[];
  };
  unknownDocument: {
    data: DocumentRecord[];
  };
};

export type DocumentMetaDataRecord = {
  metadata: {
    loadStatus: string;
    billedDate: string;
    orderStatus: string;
    orderInvoicedDate: string;
    carrier: string;
    quickPay: boolean | null;
  };
  uploaded: UploadedDocuments;
  required: string[];
};

export type StagingDocumentsFlyoutTableRow = {
  billedDate: string;
  carrier: string;
  orderInvoicedDate: string;
  orderStatus: string;
  loadStatus: string;
};

// Billing Queue Filter Persistence Types
export type BillingQueueTabKey =
  | 'UploadQueue'
  | 'Approved'
  | 'ReviewDocuments'
  | 'UnknownDocuments'
  | 'Responded'
  | 'PendingApprovals'
  | 'Disputed'
  | 'DocumentHistory';

export const BILLING_QUEUE_TAB_KEYS: readonly BillingQueueTabKey[] = [
  'UploadQueue',
  'Approved',
  'ReviewDocuments',
  'UnknownDocuments',
  'Responded',
  'PendingApprovals',
  'Disputed',
  'DocumentHistory',
] as const;

export type UploadQueueFilterT = {
  field: string;
  filterType: string;
  values: string[];
};

export type UploadQueueRequestT = {
  filters: UploadQueueFilterT[];
  sort: {
    field: string;
    direction: string;
  };
  pageNumber: number;
  pageSize: number;
};

export type UploadQueueResponseT = {
  data: BillingQueueUploadItem[];
  totalCount: number;
};

export type UploadQueueRowT = Omit<BillingQueueUploadItem, 'lTL'> & {
  ltl: boolean;
};

export type ApprovedQueueRowT = Omit<BillingQueueApprovedItem, 'lTL'> & {
  ltl: boolean;
};

export type ApprovedQueueResponseT = {
  data: BillingQueueApprovedItem[];
  totalCount: number;
};

export type RespondedQueueRowT = Omit<BillingQueueRespondedItem, 'lTL'> & {
  ltl: boolean;
};

export type RespondedQueueResponseT = {
  data: BillingQueueRespondedItem[];
  totalCount: number;
};
